import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { TouchableOpacity, View } from 'react-native';
import StyleSheet from 'react-native-media-query';
import DatePicker, { useDefaultStyles } from 'react-native-ui-datepicker';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigation, useRoute } from '@react-navigation/native';
import { Divider, Icon, Layout, Popover } from '@ui-kitten/components';
import dayjs, { Dayjs } from 'dayjs';
import lodashFind from 'lodash/find';
import lodashIsEmpty from 'lodash/isEmpty';

import Button from '../../../Components/Button';
import DropdownPopover from '../../../Components/DropdownPopover';
import { DropdownPopoverTypes } from '../../../Components/DropdownPopover/types';
import ListItem from '../../../Components/ListItem';
import GooglePlaces_Autocomplete from '../../../Components/Map/GooglePlacesAutocomplete';
import {
  defaultPartySizes,
  textCategory,
} from '../../../Components/Reservation/constants';
import ResponsiveText from '../../../Components/ResponsiveText';
import PopupModal from '../../../Components/Web/PopupModal';
import constants from '../../../Config/constants';
import DateHelper from '../../../Helper/Date';
import { filter } from '../../../RTK/defaultValues';
import {
  changeListingFilter,
  updateReservationFilter,
} from '../../../RTK/filter';
import { selectReservationFilter } from '../../../RTK/filter/selectors';
import { activeAddressSelector } from '../../../RTK/user/selectors';
import ReservationLocateOnMap from '../../../Screens/ReservationLocateOnMap';
import ThemeColor from '../../../Theme/colors';
import ThemeStyle from '../../../Theme/styles';
import breakpoints from '../../../Theme/styles/breakpoints';
import {
  ids as platformStyleIds,
  styles as platformStyles,
} from '../../../Theme/styles/platform';

import useController from '../../Address/controller/addEditAddress';

const { eventFilterData, RESERVATION_TYPE, RESERVATION_TYPES } = constants;
const reservationTypes = [
  filter.reservationFilterValue.reservationType,
  ...RESERVATION_TYPES,
];

const getDatePlaceHolder = (date: Dayjs) => {
  const isToday = dayjs().isSame(date, 'day');
  return isToday ? 'Today' : dayjs(date).format('MMM DD YYYY');
};

type DropdownTypes = Omit<DropdownPopoverTypes, 'trigger'> & {
  label: string;
  type: string;
  getContentContainerStyle?: (type: string) => void;
};
const Dropdown = ({
  data,
  label,
  type,
  getContentContainerStyle,
  onClose,
  onOpen,
  onSelect,
  ...rest
}: DropdownTypes) => {
  const [isVisible, setVisible] = useState(false);
  const Trigger = useMemo(
    () => (
      <Layout
        dataSet={{ media: ids.inputGroupWrapper }}
        style={getContentContainerStyle(type)}
      >
        <Layout style={styles.inputGroup} dataSet={{ media: ids.inputGroup }}>
          <ResponsiveText
            category={textCategory.BODY}
            status="info"
            style={{
              ...ThemeStyle.flex1,
              ...styles.filterTitles,
            }}
          >
            {label}
          </ResponsiveText>
          <ListItem
            contentContainerStyle={ThemeStyle.pageHorizontalSpacingSmall}
            title={
              <ResponsiveText category={textCategory.BODY}>
                {(rest.value?.label || rest.value) as string}
              </ResponsiveText>
            }
            rightIcon={{
              name: isVisible ? 'chevron-up' : 'chevron-down',
              size: styles.icon.width,
            }}
          />
        </Layout>
      </Layout>
    ),
    [isVisible, label, rest.value, type, getContentContainerStyle],
  );
  const _onClose = useCallback(() => {
    setVisible(false);
    onClose(null);
  }, [onClose]);
  const _onOpen = useCallback(() => {
    setVisible(true);
    onOpen(type);
  }, [type, onOpen]);
  return (
    <View style={ThemeStyle.flex1}>
      <DropdownPopover
        {...rest}
        data={data}
        trigger={Trigger}
        onClose={_onClose}
        onOpen={_onOpen}
        onSelect={onSelect}
      />
    </View>
  );
};

const EventsFilterSection = ({ onSearch }: { onSearch?: () => void }) => {
  const listingFilter = useSelector(selectReservationFilter);
  const activeAddress = useSelector(activeAddressSelector);

  const defaultStyles = useDefaultStyles();
  const dispatch = useDispatch();
  const navigation = useNavigation();
  const route = useRoute();

  const activeAddressLocation = {
    place_id: activeAddress?.place_id,
    label: activeAddress?.label,
    formatted_address: activeAddress?.formatted_address,
    latitude: activeAddress?.geo_json_point?.coordinates?.[1],
    longitude: activeAddress?.geo_json_point?.coordinates?.[0],
  };
  const { autoCompleteRef, state, onSearchItemPressed } = useController({
    navigation,
    route,
    selectedAddress: listingFilter?.location || activeAddressLocation,
  });
  const { selectedAddress, submitting } = state;

  const [selectedReservationType, setSelectedReservationType] = useState(
    listingFilter?.reservationType || reservationTypes[0],
  );
  const isEventSelected =
    selectedReservationType?.value === RESERVATION_TYPE.EVENT;
  const dateFilter = DateHelper.getDateFilter(
    selectedReservationType?.value,
    listingFilter.date,
  );
  const [selectedPartySize, setSelectedPartySize] = useState(
    defaultPartySizes[1],
  );
  const [selectedDate, setSelectedDate] = useState(
    dayjs(listingFilter.date || DateHelper.getNextMealTimeAsDayjs()),
  );
  const [datePlaceHolder, setDatePlaceHolder] = useState(
    getDatePlaceHolder(selectedDate),
  );
  const [selectedRange, setSelectedEventRange] = useState(
    lodashFind(eventFilterData, { label: listingFilter.dateText }) ||
      eventFilterData[1],
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  const [mapPickerOpen, setMapPickerOpen] = useState(false);
  const [showLocateOnMap, setShowLocateOnMap] = useState<boolean>(false);
  const timeSlots = useMemo(
    () =>
      DateHelper.getTimeSlots(selectedDate).map((ts) => ({
        label: ts,
        value: ts,
      })),
    [selectedDate],
  );
  const defaultTime = listingFilter.time
    ? dayjs(listingFilter.time, constants.DBTIME_FORMAT).format(
        constants.TIME_ONLY_DISPLAY_CAPITAL_FORMAT,
      )
    : DateHelper.getNextMealTimeAsString(
        constants.TIME_ONLY_DISPLAY_CAPITAL_FORMAT,
      );
  const [selectedTime, setSelectedTime] = useState({
    label: defaultTime,
    value: defaultTime,
  });
  const [focusedField, setFocusedField] = useState<undefined | string>();

  // Hooks for updating default value of filter
  useEffect(() => {
    const batchUpdate = [];
    // add default address using active address if there is no filter location yet
    if (
      !lodashIsEmpty(activeAddress) &&
      lodashIsEmpty(listingFilter?.location)
    ) {
      const [longitude, latitude] =
        activeAddress?.geo_json_point.coordinates || [];
      batchUpdate.push({
        key: 'location',
        value: {
          address: activeAddress?.formatted_address,
          latitude,
          longitude,
          place_id: activeAddress?.place_id,
        },
      });
    }
    // update date if nothing is set yet
    if (!listingFilter.date) {
      batchUpdate.push({
        key: 'date',
        value: dateFilter,
      });
      _updateDate(dayjs(dateFilter)); // update local state
    }
    // update time if nothing is set yet
    if (!listingFilter.time && !isEventSelected) {
      const timeValue = filter.reservationFilterValue.time;
      batchUpdate.push({
        key: 'time',
        value: timeValue,
      });
      setSelectedTime({ label: timeValue, value: timeValue }); // update local state
    }
    // update date and time filter if its past date
    if (!isEventSelected && listingFilter.date && listingFilter.time) {
      const filterDayjs = dayjs(`${listingFilter.date} ${listingFilter.time}`);
      if (dayjs().isSameOrAfter(filterDayjs, 'minute')) {
        const nextTs = DateHelper.getNextMealTimeAsDayjs();
        // update date today
        batchUpdate.push({
          key: 'date',
          value: nextTs.format(constants.DBDATE_FORMAT),
        });
        // update time to next meal plan
        batchUpdate.push({
          key: 'time',
          value: nextTs.format(constants.DBTIME_FORMAT),
        });
        // update local state
        _updateDate(nextTs);
        const formattedTimeNextTs = nextTs.format(
          constants.TIME_ONLY_DISPLAY_CAPITAL_FORMAT,
        );
        setSelectedTime({
          label: formattedTimeNextTs,
          value: formattedTimeNextTs,
        });
      }
    }
    // batch update the filter
    if (!lodashIsEmpty(batchUpdate)) {
      dispatch(updateReservationFilter({ batch: batchUpdate }));
    }
  }, [
    activeAddress,
    dateFilter,
    isEventSelected,
    listingFilter.date,
    listingFilter.location,
    listingFilter.time,
    dispatch,
  ]);

  const getInputGroupWrapperStyle = useCallback(
    (field: string) => {
      const isFocused = focusedField === field;
      return [
        styles.inputGroupWrapper,
        isFocused && {
          borderColor: ThemeColor.purple,
          boxSizing: 'border-box',
        },
      ];
    },
    [focusedField],
  );

  const filterChange = useCallback(() => {
    onSearch?.();
    dispatch(
      changeListingFilter({
        reservationType: selectedReservationType,
        location: {
          ...selectedAddress,
          address: selectedAddress.address || activeAddress?.formatted_address,
        },
        date: isEventSelected
          ? dayjs()
              .add(selectedRange.value, 'day')
              .format(constants.DBDATE_FORMAT)
          : dayjs(selectedDate).format(constants.DBDATE_FORMAT),
        dateText: selectedRange.label,
        time: dayjs(
          selectedTime.value,
          constants.TIME_ONLY_DISPLAY_CAPITAL_FORMAT,
        ).format(constants.DBTIME_FORMAT),
        party_size: selectedPartySize.value,
      }),
    );
  }, [
    activeAddress?.formatted_address,
    isEventSelected,
    selectedAddress,
    selectedDate,
    selectedPartySize.value,
    selectedRange.label,
    selectedRange.value,
    selectedReservationType,
    selectedTime.value,
    dispatch,
    onSearch,
  ]);

  // Locate on Map
  const _closeLocateOnMap = useCallback(
    (...args) => {
      if (!lodashIsEmpty(args)) {
        onSearchItemPressed(...args);
      }
      setShowLocateOnMap(false);
      setMapPickerOpen(false);
      setFocusedField(null);
    },
    [onSearchItemPressed],
  );
  const _locateOnMap = useCallback(() => {
    setMapPickerOpen(false);
    setShowLocateOnMap(true);
  }, []);
  const _showSearchLocation = useCallback(() => {
    setMapPickerOpen(true);
    setFocusedField('location');
  }, []);

  const _updateDate = (date?: Dayjs) => {
    setDatePlaceHolder(getDatePlaceHolder(date));
    setSelectedDate(date);
  };

  const _onSelectReservationType = useCallback((type) => {
    setFocusedField(null);
    setSelectedReservationType(type);
  }, []);

  const _onSelectEventDate = useCallback((date) => {
    setFocusedField(null);
    setSelectedEventRange(date);
  }, []);

  const _onSelectTimeSlot = useCallback((ts) => {
    setFocusedField(null);
    setSelectedTime(ts);
  }, []);

  // Reservation Date Picker
  const _closeReservationDatePicker = useCallback((data?: { date: string }) => {
    if (data?.date) {
      _updateDate(dayjs(data.date));
    }
    setPickerOpen(false);
    setFocusedField(null);
  }, []);
  const _showReservationDatePicker = useCallback(() => {
    setPickerOpen(true);
    setFocusedField('datePicker');
  }, []);
  const ReservationDatePickerAnchor = useCallback(
    () => (
      <TouchableOpacity onPress={_showReservationDatePicker}>
        <Layout style={styles.toggleInputWrapper}>
          <ResponsiveText category={textCategory.BODY}>
            {datePlaceHolder}
          </ResponsiveText>
          <Icon
            name={pickerOpen ? 'chevron-up' : 'chevron-down'}
            fill={ThemeColor.black}
            style={styles.icon}
          />
        </Layout>
      </TouchableOpacity>
    ),
    [datePlaceHolder, pickerOpen, _showReservationDatePicker],
  );

  return (
    <View>
      <View style={ThemeStyle.pageVerticalSpacing}>
        <Layout style={styles.container}>
          <ResponsiveText
            category={textCategory.HEADING}
            style={styles.headerText}
          >
            What are you looking for?
          </ResponsiveText>

          <Layout
            style={styles.searchCard}
            dataSet={{ media: ids.searchCard }}
            level="1"
          >
            <Dropdown
              type="reservationType"
              getContentContainerStyle={getInputGroupWrapperStyle}
              data={reservationTypes}
              label="Reservation Type"
              selectionType="color"
              value={selectedReservationType}
              onClose={setFocusedField}
              onOpen={setFocusedField}
              onSelect={_onSelectReservationType}
            />
            <View
              dataSet={{ media: platformStyleIds.webOnly }}
              style={[styles.filterItemSeparator, platformStyles.webOnly]}
            />
            <Layout
              dataSet={{ media: ids.inputGroupWrapper }}
              style={getInputGroupWrapperStyle('location')}
            >
              <Layout
                style={styles.inputGroup}
                dataSet={{ media: ids.inputGroup }}
              >
                <ResponsiveText
                  category={textCategory.BODY}
                  style={{
                    color: ThemeColor.violet,
                    ...ThemeStyle.flex1,
                    ...styles.filterTitles,
                  }}
                >
                  Location
                </ResponsiveText>
                <TouchableOpacity onPress={_showSearchLocation}>
                  <Layout style={styles.toggleInputWrapper}>
                    <ResponsiveText
                      category={textCategory.BODY}
                      numberOfLines={1}
                    >
                      {activeAddress?.place_id === selectedAddress.place_id ||
                      !selectedAddress.address
                        ? 'Makati City, Metro Manila'
                        : selectedAddress.address}
                    </ResponsiveText>
                    <Icon
                      name={pickerOpen ? 'chevron-up' : 'chevron-down'}
                      fill={ThemeColor.black}
                      style={styles.icon}
                    />
                  </Layout>
                </TouchableOpacity>
              </Layout>
            </Layout>
            <View
              dataSet={{ media: platformStyleIds.webOnly }}
              style={[styles.filterItemSeparator, platformStyles.webOnly]}
            />
            {isEventSelected ? (
              <Dropdown
                type="eventDates"
                getContentContainerStyle={getInputGroupWrapperStyle}
                data={eventFilterData}
                label="Date"
                value={selectedRange}
                onClose={setFocusedField}
                onOpen={setFocusedField}
                onSelect={_onSelectEventDate}
              />
            ) : (
              <Fragment>
                <Layout
                  dataSet={{ media: ids.inputGroupWrapper }}
                  style={getInputGroupWrapperStyle('datePicker')}
                >
                  <Layout
                    style={styles.inputGroup}
                    dataSet={{ media: ids.inputGroup }}
                  >
                    <ResponsiveText
                      category={textCategory.BODY}
                      style={{
                        color: ThemeColor.violet,
                        ...ThemeStyle.flex1,
                        ...styles.filterTitles,
                      }}
                    >
                      Date
                    </ResponsiveText>

                    <Popover
                      visible={pickerOpen}
                      anchor={ReservationDatePickerAnchor}
                      placement="bottom end"
                      onBackdropPress={_closeReservationDatePicker}
                      style={{ marginTop: 30, marginBottom: 50 }}
                    >
                      <Layout style={styles.popoverContent}>
                        <DatePicker
                          mode="single"
                          date={selectedDate}
                          onChange={_closeReservationDatePicker}
                          minDate={new Date()}
                          styles={{
                            ...defaultStyles,
                            ...styles.datePickerContainer,
                          }}
                        />
                      </Layout>
                    </Popover>
                  </Layout>
                </Layout>
                <View
                  dataSet={{ media: platformStyleIds.webOnly }}
                  style={[styles.filterItemSeparator, platformStyles.webOnly]}
                />
                <Dropdown
                  type="timeSlot"
                  getContentContainerStyle={getInputGroupWrapperStyle}
                  data={timeSlots}
                  label="Time"
                  selectionType="color"
                  value={selectedTime}
                  onClose={setFocusedField}
                  onOpen={setFocusedField}
                  onSelect={_onSelectTimeSlot}
                />
              </Fragment>
            )}
            <View
              dataSet={{ media: platformStyleIds.webOnly }}
              style={[styles.filterItemSeparator, platformStyles.webOnly]}
            />
            <Dropdown
              type="partySize"
              getContentContainerStyle={getInputGroupWrapperStyle}
              data={defaultPartySizes}
              label="Party Size"
              selectionType="color"
              value={selectedPartySize}
              onClose={setFocusedField}
              onOpen={setFocusedField}
              onSelect={setSelectedPartySize}
            />
            <View
              dataSet={{ media: platformStyleIds.webOnly }}
              style={[styles.filterItemSeparator, platformStyles.webOnly]}
            />
            <Layout
              dataSet={{ media: ids.searchButtonContainer }}
              style={styles.searchButtonContainer}
            >
              <Button
                status="info"
                style={styles.searchButton}
                onPress={filterChange}
              >
                <ResponsiveText category={textCategory.HEADING}>
                  Search
                </ResponsiveText>
              </Button>
            </Layout>
          </Layout>
        </Layout>
      </View>

      <Divider style={ThemeStyle.sectionSeparator} />

      <PopupModal
        isOpen={mapPickerOpen}
        onClosed={_closeLocateOnMap}
        title={'Choose your location'}
        withCloseButton={false}
        headerStyle={{ justifyContent: 'center' }}
        closeOnDocumentClick
      >
        <Layout
          style={[
            ThemeStyle.flex1,
            ThemeStyle.spacingBottom,
            { minHeight: 300 },
          ]}
        >
          <GooglePlaces_Autocomplete
            ref={!submitting ? autoCompleteRef : null}
            initialValue={selectedAddress.address}
            onSelected={_closeLocateOnMap}
            iconFill={ThemeColor.purple}
            // style={styles.googleplace_autocomplete}
          />
          <ListItem
            contentContainerStyle={ThemeStyle.alignSelfEnd}
            leftIcon={{
              name: 'navigation-2-outline',
              color: ThemeColor.violet,
            }}
            title={<ResponsiveText status="info">Locate on Map</ResponsiveText>}
            onPress={_locateOnMap}
          />
        </Layout>
      </PopupModal>

      {/* For locate on map modal */}
      <PopupModal
        isOpen={showLocateOnMap}
        onClosed={_closeLocateOnMap}
        hasHeader={false}
        withCloseButton={false}
        baseWidth="95vw"
        closeOnDocumentClick
      >
        <View style={[ThemeStyle.spacingTopMedium, { minHeight: '75vh' }]}>
          <ReservationLocateOnMap
            navigation={navigation}
            onClose={_closeLocateOnMap}
          />
        </View>
      </PopupModal>
    </View>
  );
};
const { ids, styles } = StyleSheet.create({
  partySizeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between', // or 'space-evenly' depending on your desired spacing
    width: '100%', // make it fill the parent container
  },
  plusMinusContainer: {
    ...ThemeStyle.pageVerticalSpacingSmall,
    ...ThemeStyle.pageHorizontalSpacingSmall,
  },
  plusMinusIcon: {
    width: 14,
    height: 14,
  },
  partySizeInput: {
    minWidth: 60,
    textAlign: 'center',
    fontSize: 15,
    color: ThemeColor.black,
    borderBottomWidth: 0, // Remove underline
  },

  selectView: {
    maxHeight: 500,
  },
  filterTitles: {
    marginLeft: 8,
  },

  container: {
    flex: 1,
    backgroundColor: '#fff',
  },
  headerText: {
    marginBottom: 10,
  },
  filterItemSeparator: {
    backgroundColor: '#9e9696',
    height: '80%',
    width: 1,
  },
  inputGroupWrapper: {
    borderWidth: 3,
    borderColor: 'transparent',
    [`@media ${breakpoints.lg}`]: {
      // only on bigger screen due to change of flex direction on smaller screen
      flex: 1,
    },
  },
  searchCard: {
    flexDirection: 'column',
    flexWrap: 'nowrap',
    borderRadius: 3,
    borderWidth: 0,
    ...ThemeStyle.shadowOverAll,
    backgroundColor: '#fff',
    [`@media ${breakpoints.lg}`]: {
      flexDirection: 'row',
      flexWrap: 'nowrap',
      alignItems: 'center',
    },
  },
  inputGroup: {
    ...ThemeStyle.pageVerticalSpacingMedium,
    ...ThemeStyle.pageHorizontalSpacingSmall,
    flex: 1,
  },
  searchButtonContainer: {
    ...ThemeStyle.pageVerticalSpacing,
    ...ThemeStyle.pageHorizontalSpacing,
    [`@media ${breakpoints.lg}`]: {
      paddingVertical: 0,
    },
  },
  searchButton: {
    borderRadius: 7,
  },
  icon: {
    height: 20,
    width: 20,
  },
  popoverContent: {
    padding: 12,
    backgroundColor: '#fff',
    borderRadius: 8,
  },
  datePickerContainer: {
    today: {
      borderColor: ThemeColor.gray,
      backgroundColor: ThemeColor.lightGray,
    }, // Add a border to today's date
    selected: {
      backgroundColor: ThemeColor.purple,
      borderRadius: 10,
    }, // Highlight the selected day
    selected_label: {
      color: ThemeColor.white,
    },
    month_selector_label: {
      color: ThemeColor.purple,
      fontSize: 18,
    },
    year_selector_label: {
      color: ThemeColor.purple,
      fontSize: 18,
    },
  },
  toggleInputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#fff',
    marginHorizontal: 7,
  },
  mapContainer: {
    marginTop: 20,
    borderRadius: 10,
    alignSelf: 'center',
    width: 500,
    height: '40vh',
  },
});

export default EventsFilterSection;
